<template>
    <div class="admin-textbook-dashboard animation-fadeIn">
        <el-tabs v-model="currentView" class="modern-tabs">
            <el-tab-pane label="教材版本" name="versions" />
            <el-tab-pane label="教材管理" name="textbooks" />
            <el-tab-pane label="章节管理" name="chapters" />
            <el-tab-pane label="知识点管理" name="knowledge" />
            <el-tab-pane label="标签管理" name="tags" />
        </el-tabs>

        <div class="tab-content-container">

            <!-- 教材版本管理 -->
            <div v-if="currentView === 'versions'" class="content-panel">
                <el-card shadow="never" class="modern-card">
                    <template #header>
                        <div class="card-header">
                            <span class="card-title">教材版本管理</span>
                            <el-button type="primary" @click="addVersion">
                                <el-icon>
                                    <Plus />
                                </el-icon> 新增版本
                            </el-button>
                        </div>
                    </template>
                    <el-table :data="versions" style="width: 100%" class="custom-table" v-loading="loading">
                        <el-table-column prop="id" label="ID" width="100" align="center" />
                        <el-table-column prop="name" label="版本名称" min-width="200" />
                        <el-table-column label="操作" width="200" align="center">
                            <template #default="scope">
                                <el-button link type="primary" @click="editVersion(scope.row)">编辑</el-button>
                                <el-button link type="danger" @click="deleteVersion(scope.row)">删除</el-button>
                            </template>
                        </el-table-column>
                    </el-table>
                </el-card>
            </div>

            <!-- 教材管理 -->
            <div v-if="currentView === 'textbooks'" class="content-panel">
                <el-card shadow="never" class="modern-card">
                    <template #header>
                        <div class="card-header">
                            <div class="header-left">
                                <span class="card-title">教材管理</span>
                                <el-select v-model="filterVersionId" placeholder="筛选版本" clearable
                                    style="width: 180px; margin-left: 20px;">
                                    <template #prefix><el-icon>
                                            <Filter />
                                        </el-icon></template>
                                    <el-option v-for="v in versions.filter(i => i && i.id !== undefined)" :key="v.id"
                                        :label="v.name" :value="v.id" />
                                </el-select>
                            </div>
                            <el-button type="primary" @click="addTextbook">
                                <el-icon>
                                    <Plus />
                                </el-icon> 新增教材
                            </el-button>
                        </div>
                    </template>
                    <el-table :data="filteredTextbooks" style="width: 100%" class="custom-table" v-loading="loading">
                        <el-table-column prop="id" label="ID" width="100" align="center" />
                        <el-table-column prop="name" label="教材名称" min-width="200" />
                        <el-table-column prop="textbookVersionId" label="所属版本" width="180">
                            <template #default="scope">
                                <el-tag size="small" plain>{{ getVersionName(scope.row.textbookVersionId) }}</el-tag>
                            </template>
                        </el-table-column>
                        <el-table-column label="操作" width="200" align="center">
                            <template #default="scope">
                                <el-button link type="primary" @click="editTextbook(scope.row)">编辑</el-button>
                                <el-button link type="danger" @click="deleteTextbook(scope.row)">删除</el-button>
                            </template>
                        </el-table-column>
                    </el-table>
                </el-card>
            </div>

            <!-- 章节管理 -->
            <div v-if="currentView === 'chapters'" class="content-panel">
                <el-card shadow="never" class="modern-card">
                    <template #header>
                        <div class="card-header">
                            <div class="header-left">
                                <span class="card-title">章节体系管理</span>
                                <div class="filter-group" style="margin-left: 20px;">
                                    <el-select v-model="chapterFilterVersionId" placeholder="选择版本"
                                        style="width: 180px; margin-right: 12px" @change="onChapterFilterVersionChange">
                                        <el-option v-for="v in versions.filter(i => i && i.id !== undefined)"
                                            :key="v.id" :label="v.name" :value="v.id" />
                                    </el-select>
                                    <el-select v-model="selectedTextbookId" placeholder="选择教材" style="width: 220px">
                                        <el-option
                                            v-for="t in filteredTextbooksForChapterFilter.filter(i => i && i.id !== undefined)"
                                            :key="t.id" :label="t.name" :value="t.id" />
                                    </el-select>
                                </div>
                            </div>
                            <el-button type="primary" @click="showAddChapterDialog">
                                <el-icon>
                                    <Plus />
                                </el-icon> 新增章节
                            </el-button>
                        </div>
                    </template>

                    <div v-if="selectedTextbookId" class="tree-container">
                        <el-tree :data="currentChapters" node-key="id" default-expand-all :expand-on-click-node="false"
                            :props="{ label: 'label', children: 'children' }" class="modern-tree">
                            <template #default="{ node, data }">
                                <div class="tree-node-content">
                                    <span class="node-label">
                                        <el-tag size="small" v-if="data.type === 'chapter'" type="primary" effect="dark"
                                            class="type-tag">章</el-tag>
                                        <el-tag size="small" v-else-if="data.type === 'section'" type="success"
                                            effect="plain" class="type-tag">节</el-tag>
                                        <el-tag size="small" v-else-if="data.type === 'kp'" type="info" plain
                                            class="type-tag">知</el-tag>
                                        {{ node.label }}
                                    </span>
                                    <div class="node-actions">
                                        <el-button link type="primary" @click.stop="append(data)">添加</el-button>
                                        <el-button link type="primary" @click.stop="editNode(data)">编辑</el-button>
                                        <el-button link type="danger" @click.stop="remove(node, data)">删除</el-button>
                                    </div>
                                </div>
                            </template>
                        </el-tree>
                    </div>
                    <el-empty v-else description="请先选择教材版本和教材名称以管理章节" />
                </el-card>
            </div>

            <!-- 知识点管理 -->
            <div v-if="currentView === 'knowledge'" class="content-panel knowledge-panel">
                <el-row :gutter="20">
                    <el-col :span="10">
                        <el-card shadow="never" class="modern-card scrollable-card">
                            <template #header>
                                <div class="card-header">
                                    <span class="card-title">📚 知识点库</span>
                                    <div class="header-actions">
                                        <el-button type="primary" plain size="small"
                                            @click="showAddKnowledgePointDialog">
                                            <el-icon>
                                                <Plus />
                                            </el-icon> 新增
                                        </el-button>
                                    </div>
                                </div>
                                <div class="header-search">
                                    <el-input v-model="kpSearchKeyword" placeholder="搜索知识点..." clearable>
                                        <template #prefix><el-icon>
                                                <Search />
                                            </el-icon></template>
                                    </el-input>
                                </div>
                                <div class="header-filters">
                                    <el-checkbox v-model="showUnassociatedOnly" @change="fetchAllKnowledgePoints">
                                        仅显示未关联知识点
                                    </el-checkbox>
                                </div>
                            </template>

                            <div class="kp-list-container">
                                <div v-for="kp in searchedAllKnowledgePoints" :key="kp.id" class="kp-list-item"
                                    :draggable="true" @dragstart="onKpDragStart($event, kp)">
                                    <div class="kp-info">
                                        <el-checkbox v-model="selectedKpIds" :label="kp.id" @click.stop>
                                            <div class="kp-main-info">
                                                <span class="kp-id">#{{ kp.id }}</span>
                                                <span class="kp-name">{{ kp.name }}</span>
                                            </div>
                                            <div v-if="kp.sections && kp.sections.length > 0" class="kp-associations">
                                                <el-tag v-for="sec in kp.sections" :key="sec.id" size="small"
                                                    type="info" class="association-tag">
                                                    {{ sec.chapter?.textbook?.textbookVersion?.name }} / {{
                                                        sec.chapter?.title }} / {{ sec.name }}
                                                </el-tag>
                                            </div>
                                        </el-checkbox>
                                    </div>
                                    <div class="kp-actions">
                                        <el-button link type="primary" @click="editKnowledgePoint(kp)">编辑</el-button>
                                        <el-button link type="danger" @click="deleteKnowledgePoint(kp)">删除</el-button>
                                    </div>
                                </div>
                                <el-empty v-if="searchedAllKnowledgePoints.length === 0" description="暂无知识点" />
                            </div>
                            <div class="kp-footer">
                                共 {{ allKnowledgePoints.length }} 个知识点
                            </div>
                        </el-card>
                    </el-col>

                    <el-col :span="14">
                        <el-card shadow="never" class="modern-card">
                            <template #header>
                                <div class="card-header">
                                    <span class="card-title">📖 教材体系管理</span>
                                </div>
                                <div class="filter-bar-modern">
                                    <el-row :gutter="10">
                                        <el-col :span="6">
                                            <el-select v-model="kpFilterVersionId" placeholder="版本"
                                                @change="onKpFilterVersionChange">
                                                <el-option v-for="v in versions.filter(i => i && i.id !== undefined)"
                                                    :key="v.id" :label="v.name" :value="v.id" />
                                            </el-select>
                                        </el-col>
                                        <el-col :span="6">
                                            <el-select v-model="kpFilterTextbookId" placeholder="教材"
                                                @change="onKpTextbookChange">
                                                <el-option
                                                    v-for="t in filteredTextbooksForKpFilter.filter(i => i && i.id !== undefined)"
                                                    :key="t.id" :label="t.name" :value="t.id" />
                                            </el-select>
                                        </el-col>
                                        <el-col :span="6">
                                            <el-select v-model="kpFilterChapterId" placeholder="章"
                                                @change="onKpChapterChange">
                                                <el-option v-for="ch in kpChapters.filter(i => i && i.id !== undefined)"
                                                    :key="ch.id" :label="ch.title" :value="ch.id" />
                                            </el-select>
                                        </el-col>
                                        <el-col :span="6">
                                            <el-select v-model="kpFilterSectionId" placeholder="节"
                                                @change="onKpSectionChange">
                                                <el-option
                                                    v-for="sec in kpSections.filter(i => i && i.id !== undefined)"
                                                    :key="sec.id" :label="sec.name" :value="sec.id" />
                                            </el-select>
                                        </el-col>
                                    </el-row>
                                </div>
                            </template>

                            <div v-if="kpFilterSectionId" class="section-kp-manager">
                                <div class="manager-header">
                                    <h4 class="section-title"> 当前节：{{ getCurrentSectionName() }}</h4>
                                    <el-button type="success" plain size="small"
                                        @click="showAddKnowledgePointDialogForSection">
                                        <el-icon>
                                            <Plus />
                                        </el-icon> 创建并关联
                                    </el-button>
                                </div>

                                <div class="associated-kps">
                                    <div class="manager-header-sub">
                                        <div class="sub-title">已关联的知识点：</div>
                                        <el-button v-if="selectedKpIds.length > 0 && kpFilterSectionId" type="primary"
                                            size="small" @click="batchAssociateKps">
                                            批量关联已选 ({{ selectedKpIds.length }})
                                        </el-button>
                                    </div>
                                    <div class="kp-tag-container" @dragover.prevent="onKpDragOver"
                                        @dragleave="onKpDragLeave" @drop="onKpDrop"
                                        :class="{ 'drag-over': isDraggingOver }">
                                        <el-tag v-for="kp in filteredKnowledgePoints" :key="kp.id" closable
                                            type="success" @close="removeKpFromSection(kp)" class="modern-tag">
                                            {{ kp.name }}
                                        </el-tag>
                                        <div v-if="filteredKnowledgePoints.length === 0" class="empty-inline">
                                            {{ isDraggingOver ? '松开以关联' : '暂无关联知识点，或拖拽知识点到此处' }}
                                        </div>
                                    </div>
                                </div>

                                <div class="quick-link-section">
                                    <div class="sub-title">快速关联已有知识点：</div>
                                    <el-autocomplete v-model="quickAddKpName" :fetch-suggestions="searchExistingKps"
                                        placeholder="输入知识点名称搜索并添加..." @select="handleQuickAddKp" style="width: 100%">
                                        <template #prefix><el-icon>
                                                <Link />
                                            </el-icon></template>
                                        <template #default="{ item }">
                                            <div class="search-item">
                                                <span>{{ item.name }}</span>
                                                <span class="item-id">#{{ item.id }}</span>
                                            </div>
                                        </template>
                                    </el-autocomplete>
                                </div>
                            </div>
                            <el-empty v-else description="请先选择教材节以管理知识点关系" />
                        </el-card>

                        <!-- 最近添加 -->
                        <el-card v-if="recentKnowledgePoints.length > 0" shadow="never" class="modern-card recent-card">
                            <template #header>
                                <span class="card-title">🕐 最近添加</span>
                            </template>
                            <div class="recent-kp-tags">
                                <el-tag v-for="kp in recentKnowledgePoints" :key="kp.id" type="success" closable
                                    @close="deleteKnowledgePoint(kp)" class="modern-tag">
                                    {{ kp.name }} <span class="source-info" v-if="kp.sectionName">({{ kp.sectionName
                                    }})</span>
                                </el-tag>
                            </div>
                        </el-card>
                    </el-col>
                </el-row>
            </div>

            <!-- 标签管理 -->
            <div v-if="currentView === 'tags'" class="content-panel">
                <el-card shadow="never" class="modern-card">
                    <template #header>
                        <div class="card-header">
                            <span class="card-title">题目标签库</span>
                            <el-button type="primary" @click="showAddTagDialog">
                                <el-icon>
                                    <Plus />
                                </el-icon> 新增标签
                            </el-button>
                        </div>
                    </template>

                    <div class="tag-management">
                        <div class="tag-intro animation-fadeIn">
                            <p>🏷️ 标签系统：用于标识题目特色，已预设以下常用分类：</p>
                            <div class="tag-examples">
                                <el-tag type="success" effect="dark">新文化题</el-tag>
                                <el-tag type="warning" effect="dark">课本原题</el-tag>
                                <el-tag type="info" effect="dark">典型题</el-tag>
                                <el-tag type="danger" effect="dark">压轴题</el-tag>
                                <el-tag effect="dark">同步题</el-tag>
                            </div>
                        </div>

                        <el-table :data="allTags" style="width: 100%; margin-top: 24px;" class="custom-table"
                            v-loading="loading">
                            <el-table-column prop="id" label="ID" width="100" align="center" />
                            <el-table-column prop="name" label="标签名称">
                                <template #default="scope">
                                    <el-tag :type="getTagType(scope.row.name)" class="modern-tag">{{ scope.row.name
                                    }}</el-tag>
                                </template>
                            </el-table-column>
                            <el-table-column label="操作" width="200" align="center">
                                <template #default="scope">
                                    <el-button link type="primary" @click="editTag(scope.row)">编辑</el-button>
                                    <el-button link type="danger" @click="deleteTag(scope.row)">删除</el-button>
                                </template>
                            </el-table-column>
                        </el-table>
                    </div>
                </el-card>
            </div>
        </div>

        <!-- Add Knowledge Point Dialog -->
        <el-dialog v-model="addKpDialogVisible" title="新增知识点" width="500px">
            <el-form :model="newKpForm" label-width="100px">
                <el-form-item label="教材版本">
                    <el-select v-model="newKpForm.versionId" placeholder="选择版本" @change="onNewKpVersionChange"
                        style="width: 100%">
                        <el-option v-for="v in versions.filter(i => i && i.id !== undefined)" :key="v.id"
                            :label="v.name" :value="v.id" />
                    </el-select>
                </el-form-item>
                <el-form-item label="教材">
                    <el-select v-model="newKpForm.textbookId" placeholder="选择教材" @change="onNewKpTextbookChange"
                        style="width: 100%">
                        <el-option v-for="t in filteredTextbooksForNewKp.filter(i => i && i.id !== undefined)"
                            :key="t.id" :label="t.name" :value="t.id" />
                    </el-select>
                </el-form-item>
                <el-form-item label="章节">
                    <el-select v-model="newKpForm.chapterId" placeholder="选择章节" @change="onNewKpChapterChange"
                        style="width: 100%">
                        <el-option v-for="ch in newKpChapters.filter(i => i && i.id !== undefined)" :key="ch.id"
                            :label="ch.title" :value="ch.id" />
                    </el-select>
                </el-form-item>
                <el-form-item label="节">
                    <el-select v-model="newKpForm.sectionId" placeholder="选择节" style="width: 100%">
                        <el-option v-for="sec in newKpSections.filter(i => i && i.id !== undefined)" :key="sec.id"
                            :label="sec.name" :value="sec.id" />
                    </el-select>
                </el-form-item>
                <el-form-item label="知识点名称">
                    <el-input v-model="newKpForm.name" placeholder="请输入知识点名称" />
                </el-form-item>
            </el-form>
            <template #footer>
                <el-button @click="addKpDialogVisible = false">取消</el-button>
                <el-button type="primary" @click="createKnowledgePoint">确定</el-button>
            </template>
        </el-dialog>

        <!-- Add Chapter Dialog -->
        <el-dialog v-model="addChapterDialogVisible" title="新增章节" width="500px">
            <el-form :model="newChapterForm" label-width="100px">
                <el-form-item label="教材版本">
                    <el-select v-model="newChapterForm.versionId" placeholder="选择版本" @change="onChapterVersionChange">
                        <el-option v-for="v in versions.filter(i => i && i.id !== undefined)" :key="v.id"
                            :label="v.name" :value="v.id" />
                    </el-select>
                </el-form-item>
                <el-form-item label="教材">
                    <el-select v-model="newChapterForm.textbookId" placeholder="选择教材">
                        <el-option v-for="t in filteredTextbooksForChapter.filter(i => i && i.id !== undefined)"
                            :key="t.id" :label="t.name" :value="t.id" />
                    </el-select>
                </el-form-item>
                <el-form-item label="章节名称">
                    <el-input v-model="newChapterForm.title" placeholder="请输入章节名称（如：第一章 集合）" />
                </el-form-item>
            </el-form>
            <template #footer>
                <el-button @click="addChapterDialogVisible = false">取消</el-button>
                <el-button type="primary" @click="createChapter">确定</el-button>
            </template>
        </el-dialog>

        <!-- Edit Node Dialog -->
        <el-dialog v-model="editNodeDialogVisible" :title="editNodeTitle" width="500px">
            <el-form :model="editNodeForm" label-width="100px">
                <el-form-item label="名称">
                    <el-input v-model="editNodeForm.name" placeholder="请输入名称" />
                </el-form-item>
            </el-form>
            <template #footer>
                <el-button @click="editNodeDialogVisible = false">取消</el-button>
                <el-button type="primary" @click="updateNode">确定</el-button>
            </template>
        </el-dialog>

        <!-- Add/Edit Tag Dialog -->
        <el-dialog v-model="tagDialogVisible" :title="tagDialogMode === 'add' ? '新增标签' : '编辑标签'" width="400px">
            <el-form :model="tagForm" label-width="80px">
                <el-form-item label="标签名称">
                    <el-input v-model="tagForm.name" placeholder="请输入标签名称" />
                </el-form-item>
            </el-form>
            <template #footer>
                <el-button @click="tagDialogVisible = false">取消</el-button>
                <el-button type="primary" @click="saveTag">确定</el-button>
            </template>
        </el-dialog>
    </div>
</template>

<script setup>
import { ref, reactive, onMounted, computed, watch } from 'vue'
import { Search, Plus, Filter, Link, Edit, Delete, Search as SearchIcon } from '@element-plus/icons-vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import api, { getTextbookVersions } from '@/utils/api'

const currentView = ref('versions')

// Data from API
const versions = ref([])
const textbooks = ref([])
const chaptersData = ref({})
const recentKnowledgePoints = ref([])

// Tag Management
const allTags = ref([])
const tagDialogVisible = ref(false)
const tagDialogMode = ref('add')
const tagForm = reactive({
    id: null,
    name: ''
})

// Load initial data
onMounted(async () => {
    await loadVersions()
    await loadTextbooks()
    await fetchAllKnowledgePoints()
    await loadAllTags()
})

// ==================== Textbook Versions ====================

const loadVersions = async () => {
    try {
        const res = await getTextbookVersions()
        versions.value = res.data.versions || res.data || []
        // Set default version to 沪教版
        const defaultVersion = versions.value.find(v => v.name === '沪教版')
        if (defaultVersion) {
            filterVersionId.value = defaultVersion.id
            chapterFilterVersionId.value = defaultVersion.id
            kpFilterVersionId.value = defaultVersion.id
            // Trigger change events to load textbooks
            onChapterFilterVersionChange()
            onKpFilterVersionChange()

            // Set default textbook to 必修一 and chapter to first chapter
            await loadTextbooks()
            const defaultTextbook = textbooks.value.find(t =>
                t.name === '必修一' && t.textbookVersionId === defaultVersion.id
            )
            if (defaultTextbook) {
                selectedTextbookId.value = defaultTextbook.id
                kpFilterTextbookId.value = defaultTextbook.id
                await loadChapters(defaultTextbook.id)
                kpChapters.value = chaptersData.value[defaultTextbook.id] || []
                // Set first chapter as default
                if (kpChapters.value.length > 0) {
                    kpFilterChapterId.value = kpChapters.value[0].id
                    kpSections.value = kpChapters.value[0].sections || []
                }
            }
        }
    } catch (error) {
        ElMessage.error('加载版本失败')
        console.error(error)
    }
}

const postVersionWithFallback = async (payload) => {
    try {
        return await api.post('/api/admin/textbook-versions', payload)
    } catch (error) {
        if (error?.response?.status === 404) {
            return await api.post('/api/textbook-versions', payload)
        }
        throw error
    }
}

const putVersionWithFallback = async (id, payload) => {
    try {
        return await api.put(`/api/admin/textbook-versions/${id}`, payload)
    } catch (error) {
        if (error?.response?.status === 404) {
            return await api.put(`/api/textbook-versions/${id}`, payload)
        }
        throw error
    }
}

const deleteVersionWithFallback = async (id) => {
    try {
        return await api.delete(`/api/admin/textbook-versions/${id}`)
    } catch (error) {
        if (error?.response?.status === 404) {
            return await api.delete(`/api/textbook-versions/${id}`)
        }
        throw error
    }
}

const adminRequestWithFallback = async (method, adminUrl, legacyUrl, payload) => {
    try {
        return await api[method](adminUrl, payload)
    } catch (error) {
        if (error?.response?.status === 404) {
            return await api[method](legacyUrl, payload)
        }
        throw error
    }
}

const addVersion = () => {
    ElMessageBox.prompt('请输入版本名称', '新增版本', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
    }).then(async ({ value }) => {
        if (value) {
            try {
                await postVersionWithFallback({ name: value })
                ElMessage.success('添加成功')
                await loadVersions()
            } catch (error) {
                ElMessage.error('添加失败')
                console.error(error)
            }
        }
    }).catch(() => { })
}

const editVersion = (row) => {
    ElMessageBox.prompt('修改版本名称', '编辑版本', {
        inputValue: row.name,
        confirmButtonText: '确定',
        cancelButtonText: '取消',
    }).then(async ({ value }) => {
        if (value) {
            try {
                await putVersionWithFallback(row.id, { name: value })
                ElMessage.success('修改成功')
                await loadVersions()
            } catch (error) {
                ElMessage.error('修改失败')
                console.error(error)
            }
        }
    }).catch(() => { })
}

const deleteVersion = (row) => {
    ElMessageBox.confirm('确定删除该版本吗？', '提示', { type: 'warning' })
        .then(async () => {
            try {
                await deleteVersionWithFallback(row.id)
                ElMessage.success('删除成功')
                await loadVersions()
            } catch (error) {
                ElMessage.error('删除失败')
                console.error(error)
            }
        }).catch(() => { })
}

// ==================== Textbooks ====================

const filterVersionId = ref(null)
const filteredTextbooks = computed(() => {
    if (!filterVersionId.value) return textbooks.value
    return textbooks.value.filter(t => t.textbookVersionId === filterVersionId.value)
})

const getVersionName = (vid) => {
    const v = versions.value.find(v => v.id === vid)
    return v ? v.name : '未知'
}

const loadTextbooks = async () => {
    try {
        const res = await api.get('/api/textbooks')
        textbooks.value = res.data.textbooks || []
    } catch (error) {
        ElMessage.error('加载教材失败')
        console.error(error)
    }
}

const addTextbook = () => {
    if (!versions.value.length) {
        ElMessage.warning('请先创建教材版本')
        return
    }

    ElMessageBox.prompt('请输入教材名称', '新增教材', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
    }).then(async ({ value }) => {
        if (value) {
            try {
                const versionId = filterVersionId.value || versions.value[0].id
                await adminRequestWithFallback('post', '/api/admin/textbooks', '/api/textbooks', {
                    name: value,
                    textbookVersionId: versionId
                })
                ElMessage.success('添加成功')
                await loadTextbooks()
            } catch (error) {
                ElMessage.error('添加失败')
                console.error(error)
            }
        }
    }).catch(() => { })
}

const editTextbook = (row) => {
    ElMessageBox.prompt('修改教材名称', '编辑教材', {
        inputValue: row.name,
        confirmButtonText: '确定',
        cancelButtonText: '取消',
    }).then(async ({ value }) => {
        if (value) {
            try {
                await adminRequestWithFallback('put', `/api/admin/textbooks/${row.id}`, `/api/textbooks/${row.id}`, {
                    name: value,
                    textbookVersionId: row.textbookVersionId
                })
                ElMessage.success('修改成功')
                await loadTextbooks()
            } catch (error) {
                ElMessage.error('修改失败')
                console.error(error)
            }
        }
    }).catch(() => { })
}

const deleteTextbook = (row) => {
    ElMessageBox.confirm('确定删除该教材吗？', '提示', { type: 'warning' })
        .then(async () => {
            try {
                await adminRequestWithFallback('delete', `/api/admin/textbooks/${row.id}`, `/api/textbooks/${row.id}`)
                ElMessage.success('删除成功')
                await loadTextbooks()
            } catch (error) {
                ElMessage.error('删除失败')
                console.error(error)
            }
        }).catch(() => { })
}

// ==================== Chapters & Sections ====================

const selectedTextbookId = ref(null)
const chapterFilterVersionId = ref(null)

const filteredTextbooksForChapterFilter = computed(() => {
    if (!chapterFilterVersionId.value) return textbooks.value
    return textbooks.value.filter(t => t.textbookVersionId === chapterFilterVersionId.value)
})

const onChapterFilterVersionChange = () => {
    selectedTextbookId.value = null
}

// Watch for textbook selection change
watch(selectedTextbookId, async (newId) => {
    if (newId) {
        await loadChapters(newId)
    }
})

const loadChapters = async (textbookId) => {
    try {
        const res = await api.get('/api/chapters/by-textbook', {
            params: { textbookId }
        })
        console.log("loadChapters: ", res.data);

        const chapters = res.data.chapters || []

        // Load sections with knowledge points for each chapter
        for (const chapter of chapters) {
            if (chapter.sections && chapter.sections.length > 0) {
                // Always reload sections to get fresh knowledge points data
                const secRes = await api.get('/api/sections/by-chapter', {
                    params: { chapterId: chapter.id }
                })
                const fullSections = secRes.data.sections || []

                // Update each section with knowledge points from API response
                for (const section of chapter.sections) {
                    const fullSection = fullSections.find(s => s.id === section.id)
                    if (fullSection) {
                        section.knowledgePoints = fullSection.knowledgePoints || []
                    }
                }
            }
        }

        chaptersData.value[textbookId] = chapters
    } catch (error) {
        ElMessage.error('加载章节失败')
        console.error(error)
    }
}

// Transform data for Element Tree
const currentChapters = computed(() => {
    if (!selectedTextbookId.value || !chaptersData.value[selectedTextbookId.value]) return []

    return chaptersData.value[selectedTextbookId.value].map(ch => ({
        id: `chapter-${ch.id}`,
        label: ch.title,
        type: 'chapter',
        rawData: ch,
        children: (ch.sections || []).map(sec => ({
            id: `section-${sec.id}`,
            label: sec.name,
            type: 'section',
            rawData: sec,
            children: (sec.knowledgePoints || []).map(kp => ({
                id: `kp-${kp.id}`,
                label: kp.name,
                type: 'kp',
                rawData: kp
            }))
        }))
    }))
})

// Chapter creation
const addChapterDialogVisible = ref(false)
const newChapterForm = reactive({
    versionId: null,
    textbookId: null,
    title: ''
})

const filteredTextbooksForChapter = computed(() => {
    if (!newChapterForm.versionId) return []
    return textbooks.value.filter(t => t.textbookVersionId === newChapterForm.versionId)
})

const onChapterVersionChange = () => {
    newChapterForm.textbookId = null
}

const showAddChapterDialog = () => {
    newChapterForm.versionId = null
    newChapterForm.textbookId = null
    newChapterForm.title = ''
    addChapterDialogVisible.value = true
}

const createChapter = async () => {
    if (!newChapterForm.title || !newChapterForm.textbookId) {
        ElMessage.warning('请填写完整信息')
        return
    }

    try {
        await adminRequestWithFallback('post', '/api/admin/chapters', '/api/chapters', {
            Title: newChapterForm.title,
            TextbookID: newChapterForm.textbookId
        })
        ElMessage.success('章节创建成功')
        addChapterDialogVisible.value = false

        // Reload chapters
        if (selectedTextbookId.value) {
            await loadChapters(selectedTextbookId.value)
        }
    } catch (error) {
        ElMessage.error('创建失败')
        console.error(error)
    }
}

const append = async (data) => {
    let typeName = ''
    let apiCall = null

    if (data.type === 'chapter') {
        typeName = '节'
        apiCall = async (name) => {
            await adminRequestWithFallback('post', '/api/admin/sections', '/api/sections', {
                name,
                chapterId: data.rawData.id
            })
        }
    } else if (data.type === 'section') {
        typeName = '知识点'
        apiCall = async (name) => {
            // Create knowledge point and associate with section by passing sectionId
            await adminRequestWithFallback('post', '/api/admin/knowledgePoints', '/api/knowledgePoints', {
                name,
                sectionId: data.rawData.id
            })
        }
    } else {
        return // Cannot add to KP
    }

    ElMessageBox.prompt(`请输入${typeName}名称`, `添加${typeName}`, {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
    }).then(async ({ value }) => {
        if (value) {
            try {
                await apiCall(value)
                ElMessage.success('添加成功')
                await loadChapters(selectedTextbookId.value)
            } catch (error) {
                ElMessage.error('添加失败')
                console.error(error)
            }
        }
    }).catch(() => { })
}

const remove = async (node, data) => {
    ElMessageBox.confirm('确定删除吗？', '提示', { type: 'warning' })
        .then(async () => {
            try {
                if (data.type === 'chapter') {
                    await adminRequestWithFallback('delete', `/api/admin/chapters/${data.rawData.id}`, `/api/chapters/${data.rawData.id}`)
                } else if (data.type === 'kp') {
                    await adminRequestWithFallback('delete', `/api/admin/knowledgePoints/${data.rawData.id}`, `/api/knowledgePoints/${data.rawData.id}`)
                }
                ElMessage.success('删除成功')
                await loadChapters(selectedTextbookId.value)
            } catch (error) {
                ElMessage.error('删除失败')
                console.error(error)
            }
        }).catch(() => { })
}

// ==================== Edit Node (Chapter/Section/KP) ====================

const editNodeDialogVisible = ref(false)
const editNodeTitle = ref('')
const editNodeForm = reactive({
    id: null,
    type: '',
    name: '',
    rawData: null
})

const editNode = (data) => {
    editNodeForm.id = data.id
    editNodeForm.type = data.type
    editNodeForm.name = data.label
    editNodeForm.rawData = data.rawData

    if (data.type === 'chapter') {
        editNodeTitle.value = '编辑章节'
    } else if (data.type === 'section') {
        editNodeTitle.value = '编辑小节'
    } else if (data.type === 'kp') {
        editNodeTitle.value = '编辑知识点'
    }

    editNodeDialogVisible.value = true
}

const updateNode = async () => {
    if (!editNodeForm.name) {
        ElMessage.warning('请输入名称')
        return
    }

    try {
        if (editNodeForm.type === 'chapter') {
            await adminRequestWithFallback('put', `/api/admin/chapters/${editNodeForm.rawData.id}`, `/api/chapters/${editNodeForm.rawData.id}`, {
                title: editNodeForm.name,
                textbookId: editNodeForm.rawData.textbookId
            })
        } else if (editNodeForm.type === 'section') {
            await adminRequestWithFallback('put', `/api/admin/sections/${editNodeForm.rawData.id}`, `/api/sections/${editNodeForm.rawData.id}`, {
                name: editNodeForm.name,
                chapterId: editNodeForm.rawData.chapterId
            })
        } else if (editNodeForm.type === 'kp') {
            await adminRequestWithFallback('put', `/api/admin/knowledgePoints/${editNodeForm.rawData.id}`, `/api/knowledgePoints/${editNodeForm.rawData.id}`, {
                name: editNodeForm.name
            })
        }

        ElMessage.success('修改成功')
        editNodeDialogVisible.value = false
        await loadChapters(selectedTextbookId.value)
    } catch (error) {
        ElMessage.error('修改失败')
        console.error(error)
    }
}

// ==================== Knowledge Point Management ====================

const kpFilterVersionId = ref(null)
const kpFilterTextbookId = ref(null)
const kpFilterChapterId = ref(null)
const kpFilterSectionId = ref(null)
const kpChapters = ref([])
const kpSections = ref([])
const loadedKnowledgePoints = ref([])

// New state for redesigned UI
const allKnowledgePoints = ref([])
const kpSearchKeyword = ref('')
const hoveredKpId = ref(null)
const quickAddKpName = ref('')

const addKpDialogVisible = ref(false)
const newKpForm = reactive({
    versionId: null,
    textbookId: null,
    chapterId: null,
    sectionId: null,
    name: ''
})
const newKpChapters = ref([])
const newKpSections = ref([])

// Batch & Drag-and-Drop state
const selectedKpIds = ref([])
const isDraggingOver = ref(false)
const showUnassociatedOnly = ref(false)

const onKpDragStart = (event, kp) => {
    event.dataTransfer.setData('kpId', kp.id)
    event.dataTransfer.effectAllowed = 'link'
}

const onKpDragOver = () => {
    isDraggingOver.value = true
}

const onKpDragLeave = () => {
    isDraggingOver.value = false
}

const onKpDrop = async (event) => {
    isDraggingOver.value = false
    const kpId = event.dataTransfer.getData('kpId')
    if (!kpId) return

    if (!kpFilterSectionId.value) {
        ElMessage.warning('请先选择一个节')
        return
    }

    // Check if already associated
    if (loadedKnowledgePoints.value.find(kp => kp.id == kpId)) {
        ElMessage.info('该知识点已关联')
        return
    }

    try {
        await adminRequestWithFallback('post', `/api/admin/sections/${kpFilterSectionId.value}/knowledgePoints`, `/api/sections/${kpFilterSectionId.value}/knowledgePoints`, {
            knowledgePointId: parseInt(kpId)
        })
        ElMessage.success('关联成功')
        onKpSectionChange()
    } catch (error) {
        ElMessage.error('关联失败')
        console.error(error)
    }
}

const batchAssociateKps = async () => {
    if (!kpFilterSectionId.value) {
        ElMessage.warning('请先选择一个节')
        return
    }

    try {
        await adminRequestWithFallback('post', `/api/admin/sections/${kpFilterSectionId.value}/knowledgePoints/batch`, `/api/sections/${kpFilterSectionId.value}/knowledgePoints/batch`, {
            knowledgePointIds: selectedKpIds.value
        })
        ElMessage.success('批量关联成功')
        selectedKpIds.value = []
        onKpSectionChange()
    } catch (error) {
        ElMessage.error('批量关联失败')
        console.error(error)
    }
}

const filteredTextbooksForKpFilter = computed(() => {
    if (!kpFilterVersionId.value) return textbooks.value
    return textbooks.value.filter(t => t.textbookVersionId === kpFilterVersionId.value)
})

const filteredTextbooksForNewKp = computed(() => {
    if (!newKpForm.versionId) return []
    return textbooks.value.filter(t => t.textbookVersionId === newKpForm.versionId)
})

const filteredKnowledgePoints = computed(() => {
    return loadedKnowledgePoints.value
})

// Computed for searching all knowledge points
const searchedAllKnowledgePoints = computed(() => {
    if (!kpSearchKeyword.value) return allKnowledgePoints.value
    const keyword = kpSearchKeyword.value.toLowerCase()
    return allKnowledgePoints.value.filter(kp =>
        kp.name.toLowerCase().includes(keyword)
    )
})

// Load all knowledge points for the left panel
const fetchAllKnowledgePoints = async () => {
    try {
        const params = {}
        if (showUnassociatedOnly.value) {
            params.filter = 'unassociated'
        }
        const res = await api.get('/api/knowledgePoints', { params })
        allKnowledgePoints.value = res.data.knowledgePoints || []
    } catch (error) {
        ElMessage.error('获取知识点失败')
    }
}

// Get current section name
const getCurrentSectionName = () => {
    if (!kpFilterSectionId.value || kpSections.value.length === 0) return ''
    const section = kpSections.value.find(s => s.id === kpFilterSectionId.value)
    return section ? section.name : ''
}

// Show add knowledge point dialog pre-filled with current section
const showAddKnowledgePointDialogForSection = () => {
    newKpForm.versionId = kpFilterVersionId.value
    newKpForm.textbookId = kpFilterTextbookId.value
    newKpForm.chapterId = kpFilterChapterId.value
    newKpForm.sectionId = kpFilterSectionId.value
    newKpChapters.value = kpChapters.value
    newKpSections.value = kpSections.value
    addKpDialogVisible.value = true
}

// Search existing knowledge points for autocomplete
const searchExistingKps = (queryString, cb) => {
    const results = queryString
        ? allKnowledgePoints.value.filter(kp =>
            kp.name.toLowerCase().includes(queryString.toLowerCase()) &&
            !loadedKnowledgePoints.value.find(lkp => lkp.id === kp.id)
        )
        : []
    cb(results.map(kp => ({ value: kp.name, id: kp.id, name: kp.name })))
}

// Handle quick add existing knowledge point to section
const handleQuickAddKp = async (item) => {
    if (!kpFilterSectionId.value) {
        ElMessage.warning('请先选择一个节')
        return
    }
    try {
        await adminRequestWithFallback('post', `/api/admin/sections/${kpFilterSectionId.value}/knowledgePoints`, `/api/sections/${kpFilterSectionId.value}/knowledgePoints`, {
            knowledgePointId: item.id
        })
        ElMessage.success('关联成功')
        quickAddKpName.value = ''
        // Reload section knowledge points
        onKpSectionChange()
    } catch (error) {
        ElMessage.error('关联失败')
        console.error(error)
    }
}

// Remove knowledge point from section
const removeKpFromSection = async (kp) => {
    if (!kpFilterSectionId.value) return
    try {
        await ElMessageBox.confirm('确定要从此节移除该知识点吗？', '提示', { type: 'warning' })
        await adminRequestWithFallback('delete', `/api/admin/sections/${kpFilterSectionId.value}/knowledgePoints/${kp.id}`, `/api/sections/${kpFilterSectionId.value}/knowledgePoints/${kp.id}`)
        ElMessage.success('移除成功')
        onKpSectionChange()
    } catch (error) {
        if (error !== 'cancel') {
            ElMessage.error('移除失败')
            console.error(error)
        }
    }
}

const onKpFilterVersionChange = () => {
    kpFilterTextbookId.value = null
    kpFilterChapterId.value = null
    kpFilterSectionId.value = null
    kpChapters.value = []
    kpSections.value = []
    loadedKnowledgePoints.value = []
}

const onKpTextbookChange = async () => {
    kpFilterChapterId.value = null
    kpFilterSectionId.value = null
    kpSections.value = []

    if (kpFilterTextbookId.value) {
        await loadChapters(kpFilterTextbookId.value)
        kpChapters.value = chaptersData.value[kpFilterTextbookId.value] || []
    } else {
        kpChapters.value = []
    }
}

const onKpChapterChange = async () => {
    kpFilterSectionId.value = null
    loadedKnowledgePoints.value = []
    console.log("onKpChapterChange: ", kpFilterChapterId.value);

    if (kpFilterChapterId.value) {
        const chapter = kpChapters.value.find(ch => ch.id === kpFilterChapterId.value)
        console.log("Selected Chapter:", chapter)
        kpSections.value = chapter?.sections || []
    } else {
        kpSections.value = []
    }
}

const onKpSectionChange = async () => {
    if (kpFilterSectionId.value) {
        try {
            const res = await api.get('/api/knowledgePoints', {
                params: { sectionId: kpFilterSectionId.value }
            })
            loadedKnowledgePoints.value = res.data.knowledgePoints || []
            console.log("Loaded Knowledge Points:", loadedKnowledgePoints.value)
        } catch (error) {
            console.error("Failed to load knowledge points:", error)
            // Fallback to section's embedded knowledgePoints
            const section = kpSections.value.find(s => s.id === kpFilterSectionId.value)
            loadedKnowledgePoints.value = section?.knowledgePoints || []
        }
    } else {
        loadedKnowledgePoints.value = []
    }
}

const showAddKnowledgePointDialog = () => {
    newKpForm.versionId = null
    newKpForm.textbookId = null
    newKpForm.chapterId = null
    newKpForm.sectionId = null
    newKpForm.name = ''
    newKpChapters.value = []
    newKpSections.value = []
    addKpDialogVisible.value = true
}

const onNewKpVersionChange = () => {
    newKpForm.textbookId = null
    newKpForm.chapterId = null
    newKpForm.sectionId = null
    newKpChapters.value = []
    newKpSections.value = []
}

const onNewKpTextbookChange = async () => {
    newKpForm.chapterId = null
    newKpForm.sectionId = null
    newKpSections.value = []

    if (newKpForm.textbookId) {
        await loadChapters(newKpForm.textbookId)
        newKpChapters.value = chaptersData.value[newKpForm.textbookId] || []
    } else {
        newKpChapters.value = []
    }
}

const onNewKpChapterChange = () => {
    newKpForm.sectionId = null

    if (newKpForm.chapterId) {
        const chapter = newKpChapters.value.find(ch => ch.id === newKpForm.chapterId)
        newKpSections.value = chapter?.sections || []
    } else {
        newKpSections.value = []
    }
}

const createKnowledgePoint = async () => {
    if (!newKpForm.name || !newKpForm.sectionId) {
        ElMessage.warning('请填写完整信息')
        return
    }

    try {
        const res = await adminRequestWithFallback('post', '/api/admin/knowledgePoints', '/api/knowledgePoints', {
            name: newKpForm.name,
            sectionId: newKpForm.sectionId
        })
        ElMessage.success('知识点创建成功')

        // Get section name for display
        const currentSection = newKpSections.value.find(s => s.id === newKpForm.sectionId)
        const sectionName = currentSection?.name || ''

        // Add to recent knowledge points list
        const newKp = {
            id: res.data.knowledgePoint?.id || Date.now(),
            name: newKpForm.name,
            sectionName: sectionName
        }

        recentKnowledgePoints.value.unshift(newKp)

        // Keep dialog open and only clear the name field for easy batch adding
        newKpForm.name = ''

        // Refresh data
        if (kpFilterTextbookId.value) {
            await loadChapters(kpFilterTextbookId.value)
            kpChapters.value = chaptersData.value[kpFilterTextbookId.value] || []
            if (kpFilterChapterId.value) {
                const chapter = kpChapters.value.find(ch => ch.id === kpFilterChapterId.value)
                kpSections.value = chapter?.sections || []
            }
        }
    } catch (error) {
        ElMessage.error('创建失败')
        console.error(error)
    }
}

const editKnowledgePoint = (row) => {
    ElMessageBox.prompt('修改知识点名称', '编辑知识点', {
        inputValue: row.name,
        confirmButtonText: '确定',
        cancelButtonText: '取消',
    }).then(async ({ value }) => {
        if (value) {
            try {
                await adminRequestWithFallback('put', `/api/admin/knowledgePoints/${row.id}`, `/api/knowledgePoints/${row.id}`, { name: value })
                ElMessage.success('修改成功')
                if (kpFilterTextbookId.value) {
                    await loadChapters(kpFilterTextbookId.value)
                    kpChapters.value = chaptersData.value[kpFilterTextbookId.value] || []
                    if (kpFilterChapterId.value) {
                        const chapter = kpChapters.value.find(ch => ch.id === kpFilterChapterId.value)
                        kpSections.value = chapter?.sections || []
                    }
                }
            } catch (error) {
                ElMessage.error('修改失败')
                console.error(error)
            }
        }
    }).catch(() => { })
}

const deleteKnowledgePoint = (row) => {
    ElMessageBox.confirm('确定删除该知识点吗？', '提示', { type: 'warning' })
        .then(async () => {
            try {
                await adminRequestWithFallback('delete', `/api/admin/knowledgePoints/${row.id}`, `/api/knowledgePoints/${row.id}`)
                ElMessage.success('删除成功')
                if (kpFilterTextbookId.value) {
                    await loadChapters(kpFilterTextbookId.value)
                    kpChapters.value = chaptersData.value[kpFilterTextbookId.value] || []
                    if (kpFilterChapterId.value) {
                        const chapter = kpChapters.value.find(ch => ch.id === kpFilterChapterId.value)
                        kpSections.value = chapter?.sections || []
                    }
                }
            } catch (error) {
                ElMessage.error('删除失败')
                console.error(error)
            }
        }).catch(() => { })
}

// ==================== Tag Management ====================

const loadAllTags = async () => {
    try {
        const res = await api.get('/api/tags')
        allTags.value = res.data || []
    } catch (error) {
        console.error('加载标签失败:', error)
    }
}

const showAddTagDialog = () => {
    tagDialogMode.value = 'add'
    tagForm.id = null
    tagForm.name = ''
    tagDialogVisible.value = true
}

const editTag = (row) => {
    tagDialogMode.value = 'edit'
    tagForm.id = row.id
    tagForm.name = row.name
    tagDialogVisible.value = true
}

const saveTag = async () => {
    if (!tagForm.name.trim()) {
        ElMessage.warning('请输入标签名称')
        return
    }
    try {
        if (tagDialogMode.value === 'add') {
            await api.post('/api/tags', { name: tagForm.name })
            ElMessage.success('添加成功')
        } else {
            await api.put(`/api/tags/${tagForm.id}`, { name: tagForm.name })
            ElMessage.success('更新成功')
        }
        tagDialogVisible.value = false
        await loadAllTags()
    } catch (error) {
        ElMessage.error(tagDialogMode.value === 'add' ? '添加失败' : '更新失败')
        console.error(error)
    }
}

const deleteTag = (row) => {
    ElMessageBox.confirm('确定删除该标签吗？', '提示', { type: 'warning' })
        .then(async () => {
            try {
                await api.delete(`/api/tags/${row.id}`)
                ElMessage.success('删除成功')
                await loadAllTags()
            } catch (error) {
                ElMessage.error('删除失败')
                console.error(error)
            }
        }).catch(() => { })
}

const getTagType = (name) => {
    const types = {
        '新文化题': 'success',
        '课本原题': 'warning',
        '典型题': 'info',
        '压轴题': 'danger',
        '同步题': ''
    }
    return types[name] || ''
}

</script>

<style scoped>
.admin-textbook-dashboard {
    background-color: transparent;
}

.modern-tabs :deep(.el-tabs__nav-wrap::after) {
    display: none;
}

.modern-tabs :deep(.el-tabs__item) {
    font-size: 16px;
    height: 50px;
    line-height: 50px;
}

.tab-content-container {
    padding: 20px 0;
}

.modern-card {
    border: none;
    border-radius: 12px;
    margin-bottom: 24px;
}

.card-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.header-left {
    display: flex;
    align-items: center;
}

.card-title {
    font-size: 18px;
    font-weight: 700;
    color: #1a1a1a;
}

.custom-table {
    border-radius: 8px;
    overflow: hidden;
}

:deep(.el-table th.el-table__cell) {
    background-color: #fafafa;
    color: #333;
    font-weight: 600;
}

.tree-container {
    padding: 10px 0;
}

.modern-tree {
    background: transparent;
}

.tree-node-content {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 8px 0;
}

.node-label {
    display: flex;
    align-items: center;
    font-size: 14px;
}

.type-tag {
    margin-right: 8px;
    width: 24px;
    height: 24px;
    padding: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 4px;
}

/* Knowledge Point Specifics */
.header-search {
    margin-top: 15px;
}

.scrollable-card {
    height: calc(100vh - 250px);
}

.kp-list-container {
    height: calc(100vh - 430px);
    overflow-y: auto;
    padding-bottom: 20px;
}

.kp-list-item {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    padding: 12px;
    border-bottom: 1px solid #f0f0f0;
    transition: all 0.2s;
    cursor: grab;
}

.kp-list-item:hover {
    background-color: #f5f7fa;
}

.kp-info {
    flex: 1;
    margin-right: 12px;
}

.kp-main-info {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 4px;
}

.kp-associations {
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
    margin-top: 4px;
    padding-left: 24px;
}

.association-tag {
    font-size: 11px;
    height: 20px;
    padding: 0 4px;
}

.header-filters {
    margin-top: 8px;
    padding: 0 4px;
}

.kp-id {
    color: #909399;
    font-size: 12px;
    margin-right: 12px;
    font-family: monospace;
}

.kp-name {
    font-size: 14px;
    color: #333;
}

.kp-actions {
    opacity: 0;
    transition: opacity 0.2s;
}

.kp-list-item:hover .kp-actions {
    opacity: 1;
}

.kp-footer {
    padding: 15px 0 0;
    border-top: 1px solid #f0f0f0;
    font-size: 13px;
    color: #909399;
}

.filter-bar-modern {
    margin-top: 15px;
}

.section-kp-manager {
    padding: 10px 0;
}

.manager-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;
}

.section-title {
    margin: 0;
    color: #1a1a1a;
}

.manager-header-sub {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 12px;
}

.associated-kps {
    margin-bottom: 30px;
}

.sub-title {
    font-size: 14px;
    font-weight: 600;
    color: #606266;
    margin-bottom: 12px;
}

.kp-tag-container {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    padding: 16px;
    background: #f8fafc;
    border-radius: 8px;
    min-height: 80px;
    border: 2px dashed transparent;
    transition: all 0.3s;
}

.kp-tag-container.drag-over {
    background: #ecf5ff;
    border-color: #409eff;
    transform: scale(1.01);
}

.modern-tag {
    border-radius: 6px;
    padding: 6px 10px;
    height: auto;
}

.empty-inline {
    color: #c0c4cc;
    font-size: 13px;
}

.quick-link-section {
    padding: 20px;
    background: #fff;
    border-radius: 8px;
    border: 1px solid #f0f0f0;
}

.search-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.item-id {
    color: #909399;
    font-size: 11px;
}

.recent-card {
    margin-top: 20px;
}

.source-info {
    opacity: 0.7;
    font-size: 11px;
    margin-left: 4px;
}

.tag-intro {
    background: #f0f7ff;
    padding: 15px;
    border-radius: 12px;
    border: 1px solid #d9ecff;
}

.tag-examples {
    display: flex;
    gap: 12px;
    margin-top: 10px;
}

.animation-fadeIn {
    animation: fadeIn 0.4s ease-out;
}

@keyframes fadeIn {
    from {
        opacity: 0;
        transform: translateY(10px);
    }

    to {
        opacity: 1;
        transform: translateY(0);
    }
}
</style>
